import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { TextInput, TextInputAlign } from '~/components/ui/TextInput';
import { rage, useInputAction, useObservable } from '~/rage';
import { formatTime, InputAction } from '~/shared';
import { BankModal } from '../BankModal';
import { PinCodeDots } from '../PinCodeDots';
import {
	StyledBody,
	StyledButtonsGroup,
	StyledButtonsRow,
	StyledCodeInputError,
	StyledCodeInputWrapper,
	StyledDescription,
} from './PinCodeResetModal.styled';

type Step = 'email' | 'create' | 'confirm';

interface IPinCodeResetModalProps {
	open: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

const PinCodeResetModal: React.FC<IPinCodeResetModalProps> = ({
	open,
	onClose,
	onSuccess,
}) => {
	const pinCodeLength = useObservable(rage.bank.getPinCodeLength());
	const maskedEmail = useObservable(rage.player.getMaskedEmail());
	const resendAvailableAt = useObservable(rage.bank.getPinCodeResetResendAt());

	const [step, setStep] = useState<Step>('email');
	const [code, setCode] = useState('');
	const [token, setToken] = useState<string>('');
	const [pin, setPin] = useState('');
	const [firstPin, setFirstPin] = useState('');
	const [isPinError, setIsPinError] = useState(false);
	const [isCodeError, setIsCodeError] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [now, setNow] = useState(() => Date.now());

	const wasOpenRef = useRef(false);

	const resetState = useCallback(() => {
		setStep('email');
		setCode('');
		setToken('');
		setPin('');
		setFirstPin('');
		setIsPinError(false);
		setIsCodeError(false);
		setIsSubmitting(false);
	}, []);

	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	useInputAction(
		InputAction.CommonMenuClose,
		() => {
			if (open) handleClose();
		},
		{ allowWhileFocused: true },
	);

	useEffect(() => {
		if (open && !wasOpenRef.current) {
			wasOpenRef.current = true;
			resetState();

			const needsCodeRequest =
				!resendAvailableAt || resendAvailableAt <= Date.now();
			if (needsCodeRequest) {
				rage.bank
					.requestPinCodeReset()
					.catch((err) => rage.notifications.showException(err));
			}
		} else if (!open && wasOpenRef.current) {
			wasOpenRef.current = false;
		}
	}, [open, resendAvailableAt, resetState]);

	useEffect(() => {
		if (!open) return;
		const intervalId = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(intervalId);
	}, [open]);

	const remainingMs = useMemo(() => {
		if (!resendAvailableAt) return 0;
		return Math.max(resendAvailableAt - now, 0);
	}, [resendAvailableAt, now]);

	const isResendDisabled = remainingMs > 0;

	const resendLabel = useMemo(() => {
		if (!isResendDisabled) return 'Отправить код повторно';
		const formatted = formatTime(remainingMs, '{mm}:{ss}') || '00:00';
		return `Отправить код повторно (${formatted})`;
	}, [isResendDisabled, remainingMs]);

	useEffect(() => {
		if (!open) return;

		rage.input
			.setAnyInputFocused(true)
			.catch((err) => rage.notifications.showException(err));

		return () => {
			rage.input
				.setAnyInputFocused(false)
				.catch((err) => rage.notifications.showException(err));
		};
	}, [open]);

	useEffect(() => {
		if (!isCodeError) return;
		const timeoutId = setTimeout(() => setIsCodeError(false), 3000);
		return () => clearTimeout(timeoutId);
	}, [isCodeError]);

	useEffect(() => {
		if (!open) return;
		if (step !== 'create' && step !== 'confirm') return;
		if (isPinError) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Backspace' || e.key === 'Delete') {
				setPin((prev) => prev.slice(0, -1));
				return;
			}

			if (!/^\d$/.test(e.key)) return;

			setPin((prev) => {
				if (prev.length >= pinCodeLength) return prev;
				return (prev + e.key).slice(0, pinCodeLength);
			});
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [open, step, pinCodeLength, isPinError]);

	useEffect(() => {
		if (!isPinError) return;
		const timeoutId = setTimeout(() => {
			setIsPinError(false);
			setPin('');
		}, 3000);
		return () => clearTimeout(timeoutId);
	}, [isPinError]);

	const handleConfirmCode = useCallback(async () => {
		if (!code.trim() || isSubmitting) return;
		setIsSubmitting(true);
		try {
			const { token } = await rage.bank.confirmPinCodeResetCode(code.trim());
			setToken(token);
			setIsCodeError(false);
			setStep('create');
			setPin('');
		} catch {
			setIsCodeError(true);
			setCode('');
		} finally {
			setIsSubmitting(false);
		}
	}, [code, isSubmitting]);

	const handleResend = useCallback(async () => {
		if (isResendDisabled || isSubmitting) return;
		try {
			await rage.bank.requestPinCodeReset();
		} catch (err) {
			rage.notifications.showException(err);
		}
	}, [isResendDisabled, isSubmitting]);

	const handleContinueCreate = useCallback(() => {
		if (pin.length !== pinCodeLength) return;
		setFirstPin(pin);
		setStep('confirm');
		setPin('');
	}, [pin, pinCodeLength]);

	const handleBackToCreate = useCallback(() => {
		setStep('create');
		setPin('');
		setIsPinError(false);
	}, []);

	const handleSubmitNewPin = useCallback(async () => {
		if (pin.length !== pinCodeLength || isSubmitting) return;

		if (pin !== firstPin) {
			setIsPinError(true);
			return;
		}

		setIsSubmitting(true);
		try {
			await rage.bank.resetPinCode(token, pin);
			rage.notifications.showSuccess('Пин-код успешно изменён');
			onSuccess?.();
			onClose();
		} catch (err) {
			rage.notifications.showException(err);
		} finally {
			setIsSubmitting(false);
		}
	}, [firstPin, isSubmitting, onClose, onSuccess, pin, pinCodeLength, token]);

	useInputAction(
		InputAction.CommonMenuAccept,
		() => {
			if (!open || isSubmitting) return;

			if (step === 'email') {
				if (isCodeError || !code.trim()) return;
				handleConfirmCode();
				return;
			}

			if (step === 'create') {
				if (isPinError || pin.length !== pinCodeLength) return;
				handleContinueCreate();
				return;
			}

			if (step === 'confirm') {
				if (isPinError || pin.length !== pinCodeLength) return;
				handleSubmitNewPin();
			}
		},
		{ allowWhileFocused: true },
	);

	const title = 'Смена пин-кода';

	return (
		<BankModal open={open} title={title} onClose={handleClose}>
			{step === 'email' && (
				<StyledBody>
					<StyledDescription>
						На вашу почту был отправлен код, который вам нужно ввести в поле
						ниже
					</StyledDescription>
					<StyledCodeInputWrapper>
						<TextInput
							height={50}
							fontSize={16}
							textAlign={TextInputAlign.Center}
							placeholder={
								isCodeError
									? ''
									: maskedEmail
									? `Введите код с почты ${maskedEmail}`
									: 'Введите код с почты'
							}
							value={code}
							readOnly={isCodeError}
							onChange={(e) => {
								if (isCodeError) return;
								setCode(e.target.value);
							}}
							active={code.length > 0 && !isCodeError}
							error={isCodeError}
							padding="0 20px"
							autoFocus
						/>
						{isCodeError && (
							<StyledCodeInputError>
								Введёный код неправильный
							</StyledCodeInputError>
						)}
					</StyledCodeInputWrapper>
					<StyledButtonsGroup>
						<StyledButtonsRow>
							<Button
								color={ButtonColors.Default}
								height={ButtonHeight.Height50}
								onClick={handleClose}
							>
								<Text>Отменить</Text>
							</Button>
							<Button
								color={
									code.trim() && !isSubmitting
										? ButtonColors.Select
										: ButtonColors.Default
								}
								height={ButtonHeight.Height50}
								disabled={!code.trim() || isSubmitting}
								onClick={handleConfirmCode}
							>
								<Text>
									{code.trim() && !isSubmitting ? 'Продолжить' : 'Введите код'}
								</Text>
							</Button>
						</StyledButtonsRow>
						<Button
							color={ButtonColors.Default}
							height={ButtonHeight.Height50}
							disabled={isResendDisabled}
							onClick={handleResend}
						>
							<Text>{resendLabel}</Text>
						</Button>
					</StyledButtonsGroup>
				</StyledBody>
			)}

			{step === 'create' && (
				<StyledBody>
					<StyledDescription>
						В поле ниже вы можете придумать новый {pinCodeLength}х значный
						пин-код для входа в ваш банковский аккаунт
					</StyledDescription>
					<PinCodeDots length={pinCodeLength} active={pin.length} />
					<StyledButtonsRow style={{ marginTop: 10 }}>
						<Button
							color={ButtonColors.Default}
							height={ButtonHeight.Height50}
							onClick={handleClose}
						>
							<Text>Отменить</Text>
						</Button>
						<Button
							color={
								pin.length === pinCodeLength
									? ButtonColors.Select
									: ButtonColors.Default
							}
							height={ButtonHeight.Height50}
							disabled={pin.length !== pinCodeLength}
							onClick={handleContinueCreate}
						>
							<Text>Продолжить</Text>
						</Button>
					</StyledButtonsRow>
				</StyledBody>
			)}

			{step === 'confirm' && (
				<StyledBody>
					<StyledDescription>
						Пожалуйста, повторите ваш новый пин-код от банковского аккаунта
					</StyledDescription>
					<PinCodeDots
						length={pinCodeLength}
						active={pin.length}
						error={isPinError}
					/>
					<StyledButtonsRow style={{ marginTop: 10 }}>
						<Button
							color={ButtonColors.Default}
							height={ButtonHeight.Height50}
							onClick={handleBackToCreate}
						>
							<Text>Вернуться назад</Text>
						</Button>
						<Button
							color={
								pin.length === pinCodeLength && !isSubmitting
									? ButtonColors.Select
									: ButtonColors.Default
							}
							height={ButtonHeight.Height50}
							disabled={pin.length !== pinCodeLength || isSubmitting}
							onClick={handleSubmitNewPin}
						>
							<Text>{pinCodeLength ? 'Изменить код' : 'Повторите код'}</Text>
						</Button>
					</StyledButtonsRow>
				</StyledBody>
			)}
		</BankModal>
	);
};

export { PinCodeResetModal };
